import { connect } from 'react-redux';
import React from 'react';
import ReactDOM from 'react-dom';
import { PanelGroup } from 'react-bootstrap';
import { Common } from 'wsm-common-data';
import { ErrorBoundary } from 'wsm-error-boundary';
import { setNewRelicCustomAttribute } from '@ddc/new-relic';
import logger from 'ws-scripts/modules/logger';
import { waitForInvData } from 'wsm-srp-utilities';
import { isBrowser } from 'ws-scripts/modules/environment';
import Modals from '../components/FacetModals';
import { addAllFacetItem } from '../utilities/add-all-facet-item';
import FacetGroup from '../components/FacetGroup';
import MultiFacetGroup from '../components/MultiFacetGroup';
import NestedFacetNavigation from '../components/nested/NestedFacetNavigation';
import {
	fuelEfficiencyFacetIds,
	detailFacetLabels,
	detailFacetLabelsObject
} from '../global-constants';
import { arrayInsertSingle } from '../utilities/array-insert-single';
import '../components/style.scss';

class WidgetContainer extends React.PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			activeFacetModal: null,
			renderPortal: false,
			isOpenModal: false,
			isMutilFacetGroups: false,
			modalTitle: '',
			modalContent: {}
		};

		if (isBrowser) {
			waitForInvData(this, 'facet');
		}
		this.closeModal = this.closeModal.bind(this);
		this.openModal = this.openModal.bind(this);
	}

	componentDidMount() {
		if (isBrowser && window.DDC?.pubsub) {
			// Subscribe to ws-inv-filters modal display events
			window.DDC.pubsub.subscribe('ws-inv-filters/modal-show', () => {
				this.setState({ renderPortal: true });
			});
			window.DDC.pubsub.subscribe('ws-inv-filters/modal-hide', () => {
				this.setState({ renderPortal: false });
				if (typeof window.scrollTo !== 'undefined') {
					window.scrollTo(0, 0);
				}
			});
		}
	}

	// eslint-disable-next-line react/sort-comp
	openModal = (
		facetId,
		modalTitleMarkup = '',
		modalContentMarkup = {},
		isMutilFacetGroupsMarkup = false
	) => {
		this.setState({
			activeFacetModal: facetId,
			isOpenModal: true,
			modalTitle: isMutilFacetGroupsMarkup
				? modalTitleMarkup
				: modalContentMarkup.title,
			modalContent: modalContentMarkup,
			isMutilFacetGroups: isMutilFacetGroupsMarkup
		});
	};

	// eslint-disable-next-line react/sort-comp
	static formatFacetLabel(title) {
		return title.toLocaleUpperCase();
	}

	static mappingLabel(labels, facetId) {
		if (!detailFacetLabelsObject[facetId]) {
			return {};
		}
		const {
			detailLabel,
			subTextLabel,
			aboutLinkLabel,
			titleLabel,
			learnMoreLinkLabel
		} = detailFacetLabelsObject[facetId];
		return {
			details: labels.get(detailLabel),
			subText: labels.get(subTextLabel),
			aboutLink: `${labels.get('ABOUT')} ${labels.get(aboutLinkLabel)}`,
			title: labels.get(titleLabel),
			learnMoreLink: labels.get(learnMoreLinkLabel),
			facetId
		};
	}

	static getModalContent(labels, facetId, isGroupFacet = false, mutilFacets) {
		if (isGroupFacet) {
			const evFacetSubContents = mutilFacets
				.map((subFacet) => {
					const facetLabel = WidgetContainer.formatFacetLabel(
						subFacet?.facet?.id
					);
					return {
						title: labels.get(`EV_${facetLabel}_TITLE_VERBIAGE`),
						details: labels.get(`EV_${facetLabel}_DETAILS_VERBIAGE`)
					};
				})
				.filter((item) => item.details);
			const facetLabelName = WidgetContainer.formatFacetLabel(facetId);
			return {
				mainDetail: labels.get(`EV_${facetLabelName}_DETAILS_VERBIAGE`),
				subContent: evFacetSubContents,
				subText: labels.get(`EV_${facetLabelName}_SUB_TEXT_VERBIAGE`)
			};
		}

		return WidgetContainer.mappingLabel(labels, facetId);
	}

	static getMultiFacetModalContent(labels, groupTitleLabel, multiFacets) {
		const evFacetSubContents = multiFacets
			.map((subFacet) => {
				const facetLabel = WidgetContainer.formatFacetLabel(
					subFacet?.facet?.id
				);
				return {
					title: labels.get(`EV_${facetLabel}_TITLE_VERBIAGE`),
					details: labels.get(`EV_${facetLabel}_DETAILS_VERBIAGE`)
				};
			})
			.filter((item) => item.details);
		const facetLabelName =
			WidgetContainer.formatFacetLabel(groupTitleLabel);
		return {
			mainDetail: labels.get(`EV_${facetLabelName}_DETAILS_VERBIAGE`),
			subContent: evFacetSubContents,
			subText: labels.get(`EV_${facetLabelName}_SUB_TEXT_VERBIAGE`)
		};
	}

	/**
	 * Get modal/detail-link content for a single facet entry.
	 * Used by nested display mode (renderNestedMode).
	 * Centralizes the evInfoPageHref, normalFuelType, and classification
	 * special-case logic.
	 *
	 * NOTE: The accordion path (getFacetGroups) still has its own inline
	 * version of this logic. A follow-up refactor will wire accordion
	 * to use this helper as well.
	 *
	 * @param {Object} entry - A facetEntry from buildFacetGroupData
	 * @param {Object} labels - Labels map from Common
	 * @param {Object} prefs - Prefs object from Common
	 * @param {Object} sitemap - Sitemap map from Common
	 * @returns {Object|null} modalContent for the facet, or null
	 */
	static getSingleFacetModalContent(entry, labels, prefs, sitemap) {
		let modalContent = entry.isDetailLink
			? WidgetContainer.mappingLabel(labels, entry.facet.id)
			: null;

		// Resolve evInfoPageHref for normalFuelType
		const evInfoPageHrefAlias =
			entry.facet.id === 'normalFuelType'
				? prefs.linkToEvInfoPageAlias
				: null;
		const evInfoPageHref = sitemap.get(evInfoPageHrefAlias);

		if (evInfoPageHref) {
			modalContent = { ...modalContent, evInfoPageHref };
		}
		// If href is not available hide learn more link
		if (!evInfoPageHref && modalContent) {
			modalContent.learnMoreLink = null;
		}

		// Classification "What's Different?" link
		if (
			entry.facet.id === 'classification' &&
			prefs.showClassificationLink
		) {
			modalContent = {
				title: entry.facet.label,
				// Atm, we need to pass a value to 'details' to ensure the CommonModal,
				// which wraps the CustomMarkupModal, renders correctly.
				details: '<></>',
				facetLabel: entry.facet.id,
				aboutLink: labels.get('WHATS_DIFFERENT'),
				learnMoreLink: null
			};
		}

		// Override details for normalFuelType
		if (entry.facet.id === 'normalFuelType' && modalContent) {
			modalContent.details = labels.get(prefs?.fuelTypeEvLabelAlias);
		}

		return modalContent;
	}

	closeModal = () => {
		this.setState({
			isOpenModal: false,
			activeFacetModal: null
		});
	};

	getModalOptions = () => {
		const { isOpenModal, modalTitle, modalContent, isMutilFacetGroups } =
			this.state;
		return {
			isOpen: isOpenModal,
			onClose: this.closeModal,
			onOpen: this.openModal,
			title: modalTitle,
			children: modalContent,
			isMutilFacetGroups
		};
	};

	/**
	 * Build normalized facet data used by nested display mode.
	 * Iterates facets once and produces data objects consumed by
	 * renderNestedMode().
	 *
	 * NOTE: The accordion path (getFacetGroups) still uses its own
	 * inline logic. A follow-up refactor will wire accordion to use
	 * this shared helper as well.
	 *
	 * @param {Object} data InvData.getFacets() response
	 * @param {boolean} useMultifacetGroups Feature flag for multi-facet groups
	 * @param {Object} flags Feature flags from Common
	 * @returns {{ facetEntries, multiGroupEntry, multiGroupInsertIndex }}
	 */
	static buildFacetGroupData(data, useMultifacetGroups, flags) {
		const appliedFilters = window.DDC.InvData.getAppliedFilters();

		/* TODO:
			Multi-facet groups should be configurable and the data
			should store this information.

			Each group should have:
			- facets contained in group
			- type of display per facet (select, radio, checkbox, etc.)
		*/

		// TODO: remove temporary variable for feature flag
		const multiFacetGroupIds = useMultifacetGroups
			? fuelEfficiencyFacetIds
			: [];

		const improvedLocationFacetsFlag =
			flags?.['ws-inv-facets-improved-location-facets'];

		const isGeoLocationEnabled =
			isBrowser && !!window.DDC?.InvData?.getGeoData?.();

		const facetEntries = [];
		let multiGroupInsertIndex = 0;
		let multiGroupPanelIsCollapsed = false;

		data.facets.forEach((facet, idx) => {
			// Create "All" facet selection for single select facets
			// and set selected to true if no other facet values are selected
			// TODO THIS SHOULD BE ADDED TO THE FACET FROM WIS.
			if (!facet.multiselect) {
				// eslint-disable-next-line no-param-reassign
				facet.values = addAllFacetItem(facet.values, facet.allLabel);
			}

			const isMultiGroupFacet = multiFacetGroupIds.includes(facet.id);

			if (isMultiGroupFacet) {
				// Track insert position of first multi-group facet
				if (idx && !multiGroupInsertIndex) {
					multiGroupInsertIndex = idx;
				}
				// Set entire panel as collapsed if ANY of these facets are set to collapsed
				if (!multiGroupPanelIsCollapsed && facet.collapsed) {
					multiGroupPanelIsCollapsed = true;
				}
				return;
			}

			const facetSelections = appliedFilters.find(
				(filter) => filter.id === facet.id
			);
			const selectedValues = facetSelections
				? facetSelections.values
				: [];

			const isGeoFacet = facet.type === 'GEO';
			const isAccountId = facet.id === 'accountId';

			// accountId is only top-level when it immediately follows a GEO
			// facet in the list (adjacent). If it appears elsewhere, it renders
			// as a normal navigable facet in nested mode.
			const previousEntry = facetEntries[facetEntries.length - 1];
			const isAdjacentToGeo =
				previousEntry && previousEntry.facet.type === 'GEO';

			facetEntries.push({
				facet,
				selectedValues,
				isDetailLink:
					detailFacetLabels.includes(facet.id) &&
					!fuelEfficiencyFacetIds.includes(facet.id),
				// isTopLevel is only used by nested display mode (ignored by accordion).
				isTopLevel:
					isGeoFacet ||
					(improvedLocationFacetsFlag &&
						isGeoLocationEnabled &&
						isAccountId &&
						isAdjacentToGeo)
			});
		});

		// Build multi-facet group data.
		// Only fuel efficiency facets are supported currently
		// for multi-facet group.
		const multiGroupFacets = data.facets.filter((facet) =>
			multiFacetGroupIds.includes(facet.id)
		);

		const multiFacets = multiGroupFacets.map((mf) => {
			const fs = appliedFilters.find((filter) => filter.id === mf.id);
			return { facet: mf, facetSelections: fs };
		});

		const multiGroupEntry = multiGroupFacets.length
			? {
					facets: multiFacets,
					groupId: 'fuelEfficiency-group',
					groupTitleLabel: 'MPG_MPGE',
					collapsed: multiGroupPanelIsCollapsed
			  }
			: null;

		return {
			facetEntries,
			multiGroupEntry,
			multiGroupInsertIndex
		};
	}

	/**
	 * Create a FacetGroup component for each object in data.facets
	 *
	 * @param data {Object} InvData.getFacets() response
	 * @returns {FacetGroup[]} FacetGroup components
	 */
	static getFacetGroups(data, useMultifacetGroups, onOpen) {
		const appliedFilters = window.DDC.InvData.getAppliedFilters();
		// The following is to support grouping facets together into a single panel.
		// The only group currently is for fuelEfficiency.
		// This should be expanded to support groups configurable in composer.

		/* TODO:
			Multi-facet groups should be configurable and the data
			should store this information.

			Each group should have:
			- facets contained in group
			- type of display per facet (select, radio, checkbox, etc.)
		*/

		// TODO: remove temporary variable for feature flag
		const multiFacetGroupIds = useMultifacetGroups
			? fuelEfficiencyFacetIds
			: [];

		const singleGroupFacets = data.facets.filter(
			(facet) => !multiFacetGroupIds.includes(facet.id)
		);

		let multiGroupInsertIndex = 0;
		let multiGroupPanelIsCollapsed = false;
		const multiGroupFacets = data.facets.filter((facet, idx) => {
			const isMultiGroupFacet = multiFacetGroupIds.includes(facet.id);
			// Get index of first facet as insert position for group
			if (isMultiGroupFacet && idx && !multiGroupInsertIndex) {
				multiGroupInsertIndex = idx;
			}

			// Set entire panel as collapsed if ANY of these facets are set to collapsed
			if (
				isMultiGroupFacet &&
				!multiGroupPanelIsCollapsed &&
				facet.collapsed
			) {
				multiGroupPanelIsCollapsed = true;
			}

			return isMultiGroupFacet;
		});

		const getFacetGroup = (facets, renderGroup = true) => {
			return facets.map((facet) => {
				const facetSelections = appliedFilters.find(
					(filter) => filter.id === facet.id
				);
				// Create "All" facet selection for single select facets
				// and set selected to true if no other facet values are selected
				// TODO THIS SHOULD BE ADDED TO THE FACET FROM WIS.
				if (!facet.multiselect) {
					// eslint-disable-next-line no-param-reassign
					facet.values = addAllFacetItem(
						facet.values,
						facet.allLabel
					);
				}
				const isDetailLink =
					detailFacetLabels.includes(facet.id) &&
					!fuelEfficiencyFacetIds.includes(facet.id);
				return renderGroup ? (
					<Common
						key={`facetgroup-${facet.id}`}
						render={({ labels, prefs, sitemap }) => {
							const evInfoPageHrefAlias =
								facet.id === 'normalFuelType'
									? prefs.linkToEvInfoPageAlias
									: null;

							const evInfoPageHref =
								sitemap.get(evInfoPageHrefAlias);
							let modalContent = isDetailLink
								? WidgetContainer.getModalContent(
										labels,
										facet.id
								  )
								: null;
							if (evInfoPageHref) {
								modalContent = {
									...modalContent,
									evInfoPageHref
								};
							}
							// If href is not available hide learn more link
							if (!evInfoPageHref && modalContent) {
								modalContent.learnMoreLink = null;
							}

							if (
								facet.id === 'classification' &&
								prefs.showClassificationLink
							) {
								modalContent = {
									title: facet.label,
									// Atm, we need to pass a value to 'details' to ensure the CommonModal,
									// which wraps the CustomMarkupModal, renders correctly.
									details: '<></>',
									facetLabel: facet.id,
									aboutLink: labels.get('WHATS_DIFFERENT'),
									learnMoreLink: null
								};
							}

							if (facet.id === 'normalFuelType') {
								modalContent.details = labels.get(
									prefs?.fuelTypeEvLabelAlias
								);
							}
							return (
								<FacetGroup
									facet={facet}
									openDetailsModal={onOpen}
									detailsLinkData={modalContent}
									selectedValues={
										facetSelections
											? facetSelections.values
											: []
									}
								/>
							);
						}}
					/>
				) : (
					{
						facet,
						facetSelections
					}
				);
			});
		};
		const singlesGroup = getFacetGroup(singleGroupFacets);
		const multiFacets = getFacetGroup(multiGroupFacets, false);

		// Only fuel efficiency facets are supported currently
		// for multi-facet group
		const multiGroup = multiFacets.length ? (
			<Common
				key={`facetgroup-${multiFacets
					.map(({ facet }) => facet.id)
					.join('-')}`}
				render={({ labels }) => {
					const modalContent = WidgetContainer.getModalContent(
						labels,
						'MPG_MPGE',
						true,
						multiFacets
					);
					return (
						<MultiFacetGroup
							facets={multiFacets}
							groupId="fuelEfficiency-group"
							groupTitleLabel="MPG_MPGE"
							openDetailsModal={onOpen}
							detailsLinkData={modalContent}
							collapsed={multiGroupPanelIsCollapsed}
						/>
					);
				}}
			/>
		) : null;
		return arrayInsertSingle(
			singlesGroup,
			multiGroupInsertIndex,
			multiGroup
		);
	}

	static getFacetList(facets, options, activeFacetModal) {
		return (
			<ErrorBoundary
				errorHandler={(error, errorInfo) => {
					setNewRelicCustomAttribute(
						'SRP ERROR',
						`ws-inv-facets error boundary.\n${error}`
					);
					const newError = new Error(
						`ws-inv-facets error boundary.\n${error}`
					);
					newError.originalError = error;
					newError.originalStackTrace = errorInfo.componentStack;
					logger.error(`${newError}\n${newError.originalStackTrace}`);
				}}
			>
				{/* TODO: remove common render prop for feature flag */}
				<Common
					render={({ flags, prefs, labels, sitemap }) => {
						const useMultifacetGroups =
							flags['ws-inv-facets-use-muti-facet-groups'];
						const facetDisplay = prefs.facetDisplay || 'accordion';

						if (facetDisplay === 'nested') {
							const commonData = {
								labels,
								prefs,
								sitemap,
								flags
							};
							return (
								<>
									<Modals
										activeFacetModal={activeFacetModal}
										options={options}
									/>
									{WidgetContainer.renderNestedMode(
										facets,
										useMultifacetGroups,
										options.onOpen,
										commonData
									)}
								</>
							);
						}

						return (
							<PanelGroup id="facet-list" className="facet-list">
								<Modals
									activeFacetModal={activeFacetModal}
									options={options}
								/>
								{WidgetContainer.getFacetGroups(
									facets,
									useMultifacetGroups,
									options.onOpen
								)}
							</PanelGroup>
						);
					}}
				/>
			</ErrorBoundary>
		);
	}

	/**
	 * Build nested mode facet data and render NestedFacetNavigation.
	 * Top-level facets (GEO, adjacent accountId) are rendered inline,
	 * while navigable facets are rendered as title rows with detail
	 * panels. All are kept in a single ordered array — each entry
	 * carries an isTopLevel flag so the nested list component can
	 * decide how to render it.
	 *
	 * Uses the shared buildFacetGroupData() helper so that facet
	 * iteration, multi-group assembly, and modal content logic are
	 * not duplicated between accordion and nested modes.
	 *
	 * @param {Object} data InvData.getFacets() response
	 * @param {boolean} useMultifacetGroups Feature flag for multi-facet groups
	 * @param {Function} onOpen Modal open handler
	 * @param {Object} commonData Destructured Common context { labels, prefs, sitemap, flags }
	 */
	static renderNestedMode(data, useMultifacetGroups, onOpen, commonData) {
		const { labels, prefs, sitemap, flags } = commonData;
		const { facetEntries, multiGroupEntry, multiGroupInsertIndex } =
			WidgetContainer.buildFacetGroupData(
				data,
				useMultifacetGroups,
				flags
			);

		// Convert entries to the shape NestedFacetNavigation expects.
		// Top-level entries include pre-rendered FacetGroup content; navigable
		// entries use getSingleFacetModalContent for detail-link data (shared with
		// accordion mode).
		const facetGroups = facetEntries.map((entry) => {
			if (entry.isTopLevel) {
				return {
					facet: entry.facet,
					isTopLevel: true,
					renderedContent: (
						<FacetGroup
							facet={entry.facet}
							selectedValues={entry.selectedValues}
							renderPanel={false}
							isTopLevel
						/>
					)
				};
			}

			const modalContent = WidgetContainer.getSingleFacetModalContent(
				entry,
				labels,
				prefs,
				sitemap
			);

			return {
				facet: entry.facet,
				selectedValues: entry.selectedValues,
				detailsLinkData: modalContent,
				openDetailsModal: onOpen
			};
		});

		// Insert multi-facet group at the correct position (matching accordion).
		// Passes groupId and groupTitleLabel directly — the same props accordion
		// gives to MultiFacetGroup — so downstream components resolve the title
		// via useLabels().
		if (multiGroupEntry) {
			const multiGroupFacets = multiGroupEntry.facets;

			const modalContent = WidgetContainer.getMultiFacetModalContent(
				labels,
				multiGroupEntry.groupTitleLabel,
				multiGroupFacets
			);

			const multiGroupItem = {
				groupId: multiGroupEntry.groupId,
				groupTitleLabel: multiGroupEntry.groupTitleLabel,
				detailsLinkData: modalContent,
				openDetailsModal: onOpen,
				isMultiFacetGroup: true,
				multiFacets: multiGroupFacets
			};

			facetGroups.splice(multiGroupInsertIndex, 0, multiGroupItem);
		}

		return <NestedFacetNavigation facetGroups={facetGroups} />;
	}

	render() {
		const options = this.getModalOptions();
		const facets =
			(isBrowser &&
				window.DDC?.InvData?.srpReady &&
				window.DDC?.InvData?.getFacets &&
				window.DDC?.InvData?.getFacets()) ||
			undefined;
		if (!facets || Object.keys(window.DDC.InvData.facets).length === 0) {
			return null;
		}

		const [modalContainer] = document.querySelectorAll(
			'.modal-filters-react-portal'
		);

		const isRenderPortal = modalContainer && this.state.renderPortal;

		const facetList = WidgetContainer.getFacetList(
			facets,
			options,
			this.state.activeFacetModal
		);

		// Renders the `facetList` into a modal based on event type
		if (isRenderPortal) {
			return ReactDOM.createPortal(facetList, modalContainer);
		}

		return facetList;
	}
}

WidgetContainer.propTypes = {};

const mapStateToProps = (_state) => {
	const {
		requestData: { widgetName }
	} = _state;
	return { widgetName };
};

const mapDispatchToProps = (_dispatch) => ({});

const Widget = connect(mapStateToProps, mapDispatchToProps)(WidgetContainer);

export default Widget;
